/**
 * Utilities for field display names and translations
 */

const FIELD_MAPPINGS = {
  // Declaration fields
  expediente: { en: "Declaration", es: "Expediente" },
  tipo: { en: "Type", es: "Tipo" },
  fecha_registro: { en: "Registration Date", es: "Fecha Registro" },
  fecha_creacion: { en: "Creation Date", es: "Fecha Creación" },
  cliente_id: { en: "Client ID", es: "Cliente ID" },
  agente_id: { en: "Agent ID", es: "Agente ID" },
  remitente_id: { en: "Sender ID", es: "Remitente ID" },
  declaracion_1: { en: "Declaration 1", es: "Declaración 1" },
  declaracion_2: { en: "Declaration 2", es: "Declaración 2" },
  declaracion_3: { en: "Declaration 3", es: "Declaración 3" },
  aduana_entrada: { en: "Entry Customs", es: "Aduana Entrada" },
  aduana_salida: { en: "Exit Customs", es: "Aduana Salida" },
  aduana_despacho: { en: "Clearance Customs", es: "Aduana Despacho" },
  pais_destino: { en: "Destination Country", es: "País Destino" },
  pais_exportacion: { en: "Export Country", es: "País Exportación" },
  pais_transporte_frontera: { en: "Border Transport Country", es: "País Transporte Frontera" },
  modo_transporte_frontera: { en: "Border Transport Mode", es: "Modo Transporte Frontera" },
  modo_transporte_interior: { en: "Internal Transport Mode", es: "Modo Transporte Interior" },
  identidad_transporte_frontera: { en: "Border Transport Identity", es: "Identidad Transporte Frontera" },
  factura: { en: "Invoice", es: "Factura" },
  fecha_factura: { en: "Invoice Date", es: "Fecha Factura" },
  condiciones_entrega: { en: "Delivery Terms", es: "Condiciones Entrega" },
  naturaleza_transaccion: { en: "Transaction Nature", es: "Naturaleza Transacción" },
  observaciones: { en: "Observations", es: "Observaciones" },
  tiene_contenedores: { en: "Has Containers", es: "Tiene Contenedores" },
  total_bultos: { en: "Total Packages", es: "Total Bultos" },
  estado_edi: { en: "EDI Status", es: "Estado EDI" },
  estado_edi_canarias: { en: "Canary Islands EDI Status", es: "Estado EDI Canarias" },
  estado_pdc: { en: "PDC Status", es: "Estado PDC" },
  autorizacion_por_operacion: { en: "Authorization per Operation", es: "Autorización por Operación" },
  es_simplificado: { en: "Simplified", es: "Simplificado" },
  es_recapitulativo: { en: "Recapitulative", es: "Recapitulativo" },
  completo: { en: "Complete", es: "Completo" },
  es_plantilla: { en: "Is Template", es: "¿Es Plantilla?" },
  e_mail: { en: "Contact Email", es: "Correo Electrónico de Contacto" },
  contacto_telefono: { en: "Contact Phone", es: "Teléfono de Contacto" },
  pais_ubicacion: { en: "Location Country", es: "País Ubicación" },
  tipo_ubicacion: { en: "Location Type", es: "Tipo Ubicación" },
  localizacion: { en: "Location Code", es: "Código de Localización" },
  divisa: { en: "Currency Code", es: "Código Divisa" },
  cambio_divisa: { en: "Exchange Rate", es: "Cambio Divisa" },
  divisa_iso: { en: "Currency ISO", es: "Divisa ISO" },
  pais: { en: "Country", es: "País" },
  
  // Item fields
  descripcion_mercancia: { en: "Goods Description", es: "Descripción de Mercancía" },
  codigo_nc: { en: "NC Code", es: "Código NC" },
  masa_bruta: { en: "Gross Weight", es: "Masa Bruta" },
  masa_neta: { en: "Net Weight", es: "Masa Neta" },
  pais_origen: { en: "Country of Origin", es: "País de Origen" },
  cantidad_euros: { en: "Euro Amount", es: "Cantidad en Euros" },
  cantidad_divisas: { en: "Currency Amount", es: "Cantidad en Divisas" },
  precio: { en: "Price", es: "Precio" },
  provincia_origen: { en: "Province of Origin", es: "Provincia de Origen" },
  regimen_solicitado_precedente: { en: "Previous Regime", es: "Régimen Solicitado" },
  regimen_comunitario_nacional: { en: "Community/National Regime", es: "Régimen Comunitario Nacional" },
  valor_estadistico: { en: "Statistical Value", es: "Valor Estadístico" },
  adiciones: { en: "Additions", es: "Adiciones" },
  deducciones: { en: "Deductions", es: "Deducciones" },
  observaciones_arancelarias: { en: "Customs Remarks", es: "Observaciones Arancelarias" },
  observaciones_arancelarias_2: { en: "Customs Remarks 2", es: "Observaciones Arancelarias 2" },
  deducciones_base_iva: { en: "VAT Deduction Base", es: "Deducciones Base IVA" },
  
  // Document fields
  codigo_documento: { en: "Document Code", es: "Código de Documento" },
  numero: { en: "Number", es: "Número" },
  fecha: { en: "Date", es: "Fecha" },
  
  // Package fields
  cantidad: { en: "Quantity", es: "Cantidad" },
  cantidad_restante: { en: "Remaining Quantity", es: "Cantidad Restante" },
  piezas: { en: "Pieces", es: "Piezas" },
  es_interno_o_externo: { en: "Internal/External", es: "Interno/Externo" },
  marca: { en: "Mark", es: "Marca" },
  marcas_marca: { en: "Mark", es: "Marca" },
};

/**
 * Extract base field name from field path
 */
export const extractBaseFieldName = (fieldName) => {
  if (!fieldName) return '';
  
  // Handle documents_ and packages_ prefixes
  if (fieldName.startsWith('documents_')) {
    return fieldName.replace('documents_', '');
  }
  if (fieldName.startsWith('packages_marcas_marca')) {
    return 'marcas_marca';
  }
  if (fieldName.startsWith('packages_')) {
    return fieldName.replace('packages_', '');
  }
  if (fieldName.includes('.')) {
    return fieldName.split('.').pop();
  }
  if (fieldName.includes('[') && fieldName.includes(']')) {
    return fieldName.split('[')[0];
  }
  
  return fieldName;
};

/**
 * Get field display name based on field name and language
 */
export const getFieldDisplayName = (fieldName, isSpanish = false) => {
  const actualFieldName = extractBaseFieldName(fieldName);
  const lang = isSpanish ? 'es' : 'en';
  
  if (FIELD_MAPPINGS[actualFieldName]) {
    return FIELD_MAPPINGS[actualFieldName][lang];
  }
  
  // Format the field name nicely as fallback
  return actualFieldName
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

